import axiosInstance from '../fetchConfig/axiosConfig';
import logger from './logger';

/**
 * Get user's geographic location based on IP address
 * @returns {Promise<Object>} Location object with city, region, country
 */
const getUserLocation = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return {
      city: data.city || 'Unknown',
      region: data.region || 'Unknown',
      country: data.country_name || 'Unknown',
      ip: data.ip || 'Unknown'
    };
  } catch (error) {
    logger.error('Failed to get user location:', error);
    return {
      city: 'Unknown',
      region: 'Unknown', 
      country: 'Unknown',
      ip: 'Unknown'
    };
  }
};

/**
 * Sends email notification about stream retry attempts
 * @param {Object} params - Parameters for the notification
 * @param {string} params.streamType - Type of stream (e.g., 'KNN', 'General')
 * @param {Object} params.negotiationObj - Negotiation object containing metadata
 * @param {number} params.retryCount - Current retry attempt number
 * @param {number} params.maxRetries - Maximum number of retry attempts
 * @param {string} params.errorType - Type of error that occurred
 * @param {string} params.errorMessage - Detailed error message
 * @returns {Promise<void>}
 */
export const sendStreamRetryNotification = async ({
  streamType = 'General',
  negotiationObj,
  retryCount,
  maxRetries,
  errorType = 'Stream returned empty second index (stream failure detected)',
  errorMessage = ''
}) => {
  try {
    // Get user's IP-based location
    const userLocation = await getUserLocation();
    
    // Get user information from localStorage
    let user = null;
    try {
      const storedUser = localStorage.getItem('userInfo');
      user = storedUser ? JSON.parse(storedUser) : null;
    } catch (e) {
      logger.error('Failed to parse user info from localStorage:', e);
    }
    
    // Format user information
    const userName = user?.userName || user?.email || 'Unknown';
    const userEmail = user?.email || 'Not specified';
    
    await axiosInstance.post('/send_internal_email', {
      toAddress: ['jrburns@aventine.ai', 'brennan@aventine.ai'],
      subject: `${streamType} Stream Failure - Retry ${retryCount}/${maxRetries}`,
      message: `${streamType} stream processing failed and is being retried.

Details:
- MuniCode: ${negotiationObj.MuniCode || 'Not specified'}
- User: ${userName} (${userEmail})
- User Location: ${userLocation.city}, ${userLocation.region}, ${userLocation.country} (IP: ${userLocation.ip})
- TaxYear: ${negotiationObj.TaxYear || 'Not specified'}
- ReportType: ${negotiationObj.reportType || 'Not specified'}
- Attempt: ${retryCount}/${maxRetries}
- Error Type: ${errorType}
- Error Message: ${errorMessage || 'No detailed error message available'}
- Timestamp: ${new Date().toISOString()}

The system is automatically retrying the ${streamType} request.`
    });
  } catch (emailError) {
    logger.error(`Failed to send ${streamType} retry notification email:`, emailError);
  }
};

/**
 * Sends email notification about final stream failure after max retries
 * @param {Object} params - Parameters for the notification
 * @param {string} params.streamType - Type of stream (e.g., 'KNN', 'General')
 * @param {Object} params.negotiationObj - Negotiation object containing metadata
 * @param {number} params.maxRetries - Maximum number of retry attempts
 * @param {string} params.errorType - Type of error that occurred
 * @param {string} params.errorMessage - Detailed error message
 * @param {string[]} params.additionalRecipients - Additional email addresses to notify
 * @returns {Promise<void>}
 */
export const sendStreamFinalFailureNotification = async ({
  streamType = 'General',
  negotiationObj,
  maxRetries,
  errorType = 'Stream returned empty second index (failure detected)',
  errorMessage = '',
  additionalRecipients = []
}) => {
  try {
    const recipients = ['jrburns@aventine.ai', ...additionalRecipients];
    
    // Get user's IP-based location
    const userLocation = await getUserLocation();
    
    // Get user information from localStorage
    let user = null;
    try {
      const storedUser = localStorage.getItem('userInfo');
      user = storedUser ? JSON.parse(storedUser) : null;
    } catch (e) {
      logger.error('Failed to parse user info from localStorage:', e);
    }
    
    // Format user information
    const userName = user?.userName || user?.email || 'Unknown';
    const userEmail = user?.email || 'Not specified';
    
    await axiosInstance.post('/send_internal_email', {
      toAddress: recipients,
      subject: `${streamType} Stream Processing Failed After ${maxRetries} Retries`,
      message: `${streamType} stream processing has failed after maximum retry attempts.

Details:
- MuniCode: ${negotiationObj.MuniCode || 'Not specified'}
- User: ${userName} (${userEmail})
- User Location: ${userLocation.city}, ${userLocation.region}, ${userLocation.country} (IP: ${userLocation.ip})
- TaxYear: ${negotiationObj.TaxYear || 'Not specified'}
- ReportType: ${negotiationObj.reportType || 'Not specified'}
- MaxRetries: ${maxRetries}
- Error Type: ${errorType}
- Error Message: ${errorMessage || 'No detailed error message available'}
- Timestamp: ${new Date().toISOString()}

The system has exhausted all retry attempts and cannot continue ${streamType} processing.`
    });
  } catch (emailError) {
    logger.error(`Failed to send final ${streamType} failure notification email:`, emailError);
  }
};

/**
 * Convenience function to send both retry and final failure notifications
 * @param {Object} params - Parameters for the notification
 * @param {string} params.streamType - Type of stream (e.g., 'KNN', 'General')
 * @param {Object} params.negotiationObj - Negotiation object containing metadata
 * @param {number} params.retryCount - Current retry attempt number
 * @param {number} params.maxRetries - Maximum number of retry attempts
 * @param {boolean} params.isFinalFailure - Whether this is the final failure
 * @param {string} params.errorType - Type of error that occurred
 * @param {string} params.errorMessage - Detailed error message
 * @param {string[]} params.additionalRecipients - Additional email addresses for final failure
 * @returns {Promise<void>}
 */
export const sendStreamFailureNotification = async ({
  streamType = 'General',
  negotiationObj,
  retryCount,
  maxRetries,
  isFinalFailure = false,
  errorType = 'Stream returned empty second index (failure detected)',
  errorMessage = '',
  additionalRecipients = []
}) => {
  if (isFinalFailure) {
    await sendStreamFinalFailureNotification({
      streamType,
      negotiationObj,
      maxRetries,
      errorType,
      errorMessage,
      additionalRecipients
    });
  } else {
    await sendStreamRetryNotification({
      streamType,
      negotiationObj,
      retryCount,
      maxRetries,
      errorType,
      errorMessage
    });
  }
};
